import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, TrendingUp, Bell, Filter } from 'lucide-react';
import Layout from './Layout';

interface Alert {
  id: string;
  type: 'critical' | 'warning' | 'info' | 'success';
  category: string;
  title: string;
  description: string;
  timestamp: Date;
  department: string;
  impact: string;
  recommendation: string;
  read: boolean;
}

const SmartAlerts: React.FC = () => {
  const [filter, setFilter] = useState<string>('all');
  const [alerts] = useState<Alert[]>([
    {
      id: '1',
      type: 'critical',
      category: 'Emission Spike',
      title: 'Blast Furnace CO₂ Anomaly Detected',
      description: 'CO₂ emissions increased by 23% above baseline in the last 4 hours',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      department: 'Steel - Blast Furnace',
      impact: '+145 tCO₂e/day | Compliance risk',
      recommendation: 'Check oxygen enrichment system and coal quality. Consider immediate biogas injection increase.',
      read: false
    },
    {
      id: '2',
      type: 'warning',
      category: 'Compliance Threshold',
      title: 'Approaching Monthly Emission Limit',
      description: 'Current trajectory will exceed permitted limit by 8% in 12 days',
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
      department: 'Cement - Kiln',
      impact: 'Potential penalty: $125K',
      recommendation: 'Increase alternative fuel rate to 45% and reduce clinker factor to 0.72.',
      read: false
    },
    {
      id: '3',
      type: 'info',
      category: 'Optimization Opportunity',
      title: 'Waste Heat Recovery Efficiency Drop',
      description: 'WHR system operating at 78% efficiency (baseline: 92%)',
      timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000),
      department: 'Cement - Power Generation',
      impact: 'Energy loss: 2.4 MWh/day',
      recommendation: 'Schedule maintenance for heat exchanger cleaning. Potential savings: $18K/month.',
      read: true
    },
    {
      id: '4',
      type: 'success',
      category: 'Performance Achievement',
      title: 'Biogas Integration Target Exceeded',
      description: 'Coal displacement reached 48% (target: 45%)',
      timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000),
      department: 'Steel - Blast Furnace',
      impact: 'Additional reduction: 280 tCO₂e/month',
      recommendation: 'Document best practices for replication. Eligible for performance bonus credits.',
      read: true
    },
    {
      id: '5',
      type: 'warning',
      category: 'Supply Chain Risk',
      title: 'RDF Quality Degradation',
      description: 'Calorific value dropped to 3,200 kcal/kg (spec: 3,800 kcal/kg)',
      timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000),
      department: 'Circular Waste - RDF Production',
      impact: 'Kiln efficiency -12% | Coal backup +15%',
      recommendation: 'Engage supplier for feedstock quality improvement. Consider alternative waste sources.',
      read: true
    },
    {
      id: '6',
      type: 'critical',
      category: 'MRV Compliance',
      title: 'Missing Emission Data Points',
      description: 'CEMS offline for Kiln #2 for 6 hours - data gap in MRV report',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
      department: 'Cement - Monitoring',
      impact: 'Verification risk for 450 carbon credits',
      recommendation: 'Immediate CEMS repair. Use backup calculation methodology per ISO 14064.',
      read: false
    },
    {
      id: '7',
      type: 'info',
      category: 'Market Opportunity',
      title: 'Carbon Credit Price Surge',
      description: 'Article 6 credits trading at $205/tCO₂e (+12% this week)',
      timestamp: new Date(Date.now() - 30 * 60 * 60 * 1000),
      department: 'Climate Finance',
      impact: 'Portfolio value increase: +$230K',
      recommendation: 'Consider selling 2,000 credits to lock in gains. Market volatility expected.',
      read: true
    },
    {
      id: '8',
      type: 'warning',
      category: 'Predictive Alert',
      title: 'Forecasted Compliance Gap - Q2 2025',
      description: 'AI model predicts 5% overshoot of quarterly target based on current trends',
      timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000),
      department: 'Multi-Plant',
      impact: 'Projected excess: 2,100 tCO₂e',
      recommendation: 'Accelerate energy efficiency projects. Schedule production optimization workshop.',
      read: false
    }
  ]);

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'critical':
        return <AlertTriangle className="text-red-600" size={24} />;
      case 'warning':
        return <AlertTriangle className="text-yellow-600" size={24} />;
      case 'success':
        return <CheckCircle className="text-green-600" size={24} />;
      default:
        return <TrendingUp className="text-blue-600" size={24} />;
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'critical':
        return 'border-red-200 bg-red-50';
      case 'warning':
        return 'border-yellow-200 bg-yellow-50';
      case 'success':
        return 'border-green-200 bg-green-50';
      default:
        return 'border-blue-200 bg-blue-50';
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !alert.read;
    return alert.type === filter;
  });

  const unreadCount = alerts.filter(a => !a.read).length;

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Bell className="text-green-600" />
            Smart Alerts & Anomaly Detection
            {unreadCount > 0 && (
              <span className="px-3 py-1 bg-red-600 text-white text-sm rounded-full">
                {unreadCount} new
              </span>
            )}
          </h1>
          <p className="text-gray-600 mt-2">
            AI-powered real-time monitoring and predictive alerts for emission control
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-red-600 text-sm font-medium">Critical</div>
                <div className="text-2xl font-bold text-gray-900">
                  {alerts.filter(a => a.type === 'critical').length}
                </div>
              </div>
              <AlertTriangle className="text-red-600" size={32} />
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-yellow-600 text-sm font-medium">Warnings</div>
                <div className="text-2xl font-bold text-gray-900">
                  {alerts.filter(a => a.type === 'warning').length}
                </div>
              </div>
              <AlertTriangle className="text-yellow-600" size={32} />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-blue-600 text-sm font-medium">Info</div>
                <div className="text-2xl font-bold text-gray-900">
                  {alerts.filter(a => a.type === 'info').length}
                </div>
              </div>
              <TrendingUp className="text-blue-600" size={32} />
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-green-600 text-sm font-medium">Success</div>
                <div className="text-2xl font-bold text-gray-900">
                  {alerts.filter(a => a.type === 'success').length}
                </div>
              </div>
              <CheckCircle className="text-green-600" size={32} />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4">
            <Filter size={20} className="text-gray-600" />
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg ${
                filter === 'all' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              All ({alerts.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-lg ${
                filter === 'unread' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Unread ({unreadCount})
            </button>
            <button
              onClick={() => setFilter('critical')}
              className={`px-4 py-2 rounded-lg ${
                filter === 'critical' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Critical
            </button>
            <button
              onClick={() => setFilter('warning')}
              className={`px-4 py-2 rounded-lg ${
                filter === 'warning' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Warnings
            </button>
          </div>
        </div>

        {/* Alerts List */}
        <div className="space-y-4">
          {filteredAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`border rounded-lg p-6 ${getAlertColor(alert.type)} ${
                !alert.read ? 'border-l-4' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">{getAlertIcon(alert.type)}</div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-gray-600 uppercase">
                          {alert.category}
                        </span>
                        {!alert.read && (
                          <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                            NEW
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">{alert.title}</h3>
                    </div>
                    <div className="text-sm text-gray-500">
                      {alert.timestamp.toLocaleString()}
                    </div>
                  </div>

                  <p className="text-gray-700 mb-3">{alert.description}</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1">Department</div>
                      <div className="text-sm text-gray-900">{alert.department}</div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1">Impact</div>
                      <div className="text-sm text-gray-900">{alert.impact}</div>
                    </div>
                  </div>

                  <div className="bg-white bg-opacity-60 rounded-lg p-4 border border-gray-300">
                    <div className="text-xs font-medium text-gray-600 mb-1">
                      AI Recommendation
                    </div>
                    <div className="text-sm text-gray-900">{alert.recommendation}</div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button 
                      onClick={() => window.alert(`Taking action on: ${alert.title}\n\nRecommendation: ${alert.recommendation}`)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                      Take Action
                    </button>
                    <button 
                      onClick={() => window.alert(`Alert Details:\n\nCategory: ${alert.category}\nDepartment: ${alert.department}\nImpact: ${alert.impact}\n\nDescription: ${alert.description}`)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm">
                      View Details
                    </button>
                    <button 
                      onClick={() => window.alert('Alert dismissed')}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm">
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default SmartAlerts;
